// LNK-5 (Meeting Links + Return After Sign-In spec, Oct 29): is the saved login still good?
// A login lasts 24 hours from sign-in (the server's `expires`). The sign-in check only looked
// for a saved login, so an expired one opened the app and every page came up blank. This answers
// "ok", "expired", "unknown" (the server couldn't say — never a reason to log anyone out) or
// "none" (no one is logged in).

import { loginPathFor } from "./returnTo";

export type LoginVerdict = "ok" | "expired" | "unknown" | "none";

const EXPIRES_AT = "tokenExpiresAt";

// what GET /sessions/:token's answer means; the server answers 500 with this message for a
// login it no longer has
export const sessionVerdict = (status: number, body: any): LoginVerdict => {
  if (status === 200 && body && body.id) return "ok";
  if (status === 401 || /could not authorize/i.test(String(body?.error || ""))) return "expired";
  return "unknown";
};

// LNK-7: the Agents server answers 401 `invalid-token` for a login it no longer accepts.
// (401 `auth-required` means no login was sent at all; that's not an expired login.)
export const agentsVerdict = (status: number, body: any): LoginVerdict =>
  status === 401 && body?.error === "invalid-token" ? "expired" : "unknown";

// keep the login's end time, from the `expires` seconds the server sends at sign-in
export const rememberExpiry = (expiresSec: number | undefined, now: number = Date.now()) => {
  if (expiresSec && expiresSec > 0) localStorage.setItem(EXPIRES_AT, String(now + expiresSec * 1000));
};

export const forgetExpiry = () => localStorage.removeItem(EXPIRES_AT);

// the time the login in use ends: an admin's "log in as" pass has its own end time
const endsAt = (): number | null => {
  try {
    if (localStorage.getItem("adminToken")) {
      const until = JSON.parse(localStorage.getItem("adminViewing") || "null")?.until;
      return typeof until === "number" ? until : null;
    }
  } catch {
    return null;
  }
  const at = Number(localStorage.getItem(EXPIRES_AT));
  return at > 0 ? at : null;
};

// the saved end time has passed (known without asking the server)
export const loginTimePassed = (now: number = Date.now()): boolean => {
  const end = endsAt();
  return Boolean(end && now >= end);
};

export const checkLogin = async ({
  now = Date.now(),
  askServer,
}: {
  now?: number;
  askServer: (token: string) => Promise<{ status: number; body: any }>;
}): Promise<LoginVerdict> => {
  const token = localStorage.getItem("token");
  if (!token) return "none";
  const end = endsAt();
  if (end && now >= end) return "expired";
  try {
    const { status, body } = await askServer(token);
    return sessionVerdict(status, body);
  } catch {
    return "unknown";
  }
};

// LNK-6: an expired login logs out and goes to the login screen, which returns to this page
// after sign-in (LNK-1). During an admin's "log in as" visit, an expired pass returns the admin
// to their own account and the Admin screen instead. Several checks can notice at once (the
// timer, the tab coming back, a refused request); they share one ending.
let ending: Promise<void> | null = null;

export const endExpiredLogin = ({
  location = window.location,
  go = (to: string) => window.location.replace(to),
  returnToMyAccount,
}: {
  location?: { pathname: string; search: string; hash: string };
  go?: (to: string) => void;
  returnToMyAccount: () => Promise<void>;
}): Promise<void> => {
  if (ending) return ending;
  ending = (async () => {
    if (localStorage.getItem("adminToken")) {
      await returnToMyAccount();
      go("/admin");
      return;
    }
    ["token", "user", EXPIRES_AT, "adminToken", "adminUser", "adminViewing"].forEach((k) => localStorage.removeItem(k));
    go(loginPathFor(location.pathname, location.search, location.hash));
  })().finally(() => {
    ending = null;
  });
  return ending;
};
